import { useState } from 'react';
import { useImmer } from 'use-immer';
import { useEvent } from "react-use-event-hook";
import { HeaderPillButton } from '@/components/DashboardComponents'
import { Button, RoundIconButton } from '@/form/FormComponents'
import { useRevalidator } from 'react-router'
import { GenericEmptyListPlaceholder } from '@/DashboardLayout'
import { useModal, Modal, ModalContent, ModalButtons, useErrorModal } from '@/components/Modal'
import { useMenu } from '@/components/Menu'
import { Icon } from '@/components/Icon'
import { useUser } from '@/systems/Authentication'
import { publicAction } from '@/data/loaders'
import { usePreference } from '@/hooks/useStorage';
import { FileBrowserList, getRootAndPath, useNewFolderModal } from '@/pages/Filesystem';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { truncateString } from '@/shared/utils';
import { Tabs } from '@/components/DashboardComponents';
import { useBreakpoint } from '@/hooks/useIsMobile';

export const useMoveModal = () => {
  const revalidator = useRevalidator();

  const [openErrorModal] = useErrorModal();
  const [openFilePickerModal] = useFilePickerModal();

  const [addAccessConfirmModal] = useModal(function ({ context, closeModal }) {
    const { folder, callback } = context;

    function handleSubmit() {
      callback({ folder });
      closeModal();
    }

    return (
      <Modal title={"Move into a shared folder?"}>
        <ModalContent>
          {`This item will be visible to everyone who can see "${folder.title}".`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Move and share" className="primary" onClick={handleSubmit}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [removeAccessConfirmModal] = useModal(function ({ context, closeModal }) {
    const { folder, callback } = context;

    function handleSubmit() {
      callback({ folder });
      closeModal();
    }

    return (
      <Modal title={"Some people may lose access"}>
        <ModalContent>
          {`Everyone who can see "${folder.title}" will lose access to this item unless it's shared directly with them.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Move" className="primary" onClick={handleSubmit}/>
        </ModalButtons>
      </Modal>
    )
  })

  const handleOpenMoveModal = (file, parent_folder) => {
    openFilePickerModal({
      mode: "move",
      moving_id: file._shortcut_id || file._id,
      file_title: file.title,
      type: file.view.substring(0, file.view.length - 1),
      initial_folder_id: parent_folder?._id || null,
      callback: ({ folder, setLoading, closeModal }) => {
        const shared_folder_before = parent_folder?._is_shared;
        const shared_folder_after = folder?._is_shared;

        if (file._is_shortcut || file.type === "shortcut") {
          doMove();
        } else if (shared_folder_before && !shared_folder_after) {
          removeAccessConfirmModal({ folder: parent_folder, callback: doMove });
        } else if (!shared_folder_before && shared_folder_after) {
          addAccessConfirmModal({ folder, callback: doMove });
        } else {
          doMove();
        }

        function doMove() {
          setLoading(true);

          publicAction("moveFile", { id: file._shortcut_id || file._id, parent: folder?._id || null }).then(() => {
            closeModal();
            revalidator.revalidate();
          }).catch((error) => {
            console.error(error);
            setLoading(false);
            openErrorModal();
          });
        }
      }
    })
  }

  return [handleOpenMoveModal];
}

export const useFilePickerModal = () => {
  const [openFilePickerModal] = useModal(({ context, closeModal, setLoading }) => {
    return <FilePickerModal context={context} closeModal={closeModal} setLoading={setLoading}/>
  }, [])

  return [openFilePickerModal];
}

// mode: "move", "save", "choose"
// initial_folder_id: the id of the folder to start in
// type: "scenario", "rubric"
// file_title: the title of the file to move
// title: the title of the modal
// callback: a function to call when a file is chosen
  // { folder, setLoading, closeModal }
  // { file, setLoading, closeModal }

function FilePickerModal({ context, closeModal, setLoading }) {
  const { mode, initial_folder_id, type, file_title, title, callback, moving_id } = context;

  const [instance_id] = useState("filepicker_modal_"+Date.now());
  const [history, updateHistory] = useImmer(initial_folder_id ? [{ id: Date.now(), parent: initial_folder_id }] : []);
  const [folder_id, setFolderId] = useState(initial_folder_id);
  const [view, setView] = useState(type+"s");

  const handleClick = (file) => {
    if (file.type === "folder") {
      if (folder_id === file._id) {
        return;
      }
      updateHistory(draft => {
        return [...draft, { id: Date.now(), parent: file._id }];
      });
      setFolderId(file._id);
    } else {
      callback({ file, setLoading, closeModal });
    }
  }

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ['file_picker', folder_id || view],
    queryFn: () => {
      return new Promise((resolve, reject) => {
        if (folder_id || ["scenarios", "rubrics"].includes(view)) {
          publicAction("getFiles", { parent: folder_id || null, view: (folder_id ? null : view) }).then(({ data }) => {
            resolve({ ...data, view, history_id: folder_id ? history.findLast(h => h.parent === folder_id)?.id : view });
          }).catch(reject);
        } else if (view === "shared") {
          publicAction("getSharedFiles", {}).then(({ data }) => {
            resolve({ ...data, view, history_id: view });
          }).catch(reject);
        }
      })
    },
    placeholderData: keepPreviousData,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    networkMode: 'always',
    retry: false
  });

  const files = data?.value || undefined;
  const folder = data?.folder || undefined;
  const effective_view = folder?.view || data?.view || view;
  const history_id = data?.history_id;

  const [sort_initial] = usePreference(`${effective_view}_sort`, [effective_view === "shared" ? "date_shared" : "date_updated", -1]);
  const [folders_initial] = usePreference(`${effective_view}_folders`, true);

  const [local_prefs, updateLocalPrefs] = useImmer({});
  const sort = local_prefs[`${effective_view}_sort`] ?? sort_initial;
  const folders = local_prefs[`${effective_view}_folders`] ?? ((mode === "move" || mode === "save") ? true : folders_initial);

  const setSort = useEvent((value) => {
    updateLocalPrefs(draft => {
      draft[`${effective_view}_sort`] = value;
    });
  });

  const setFolders = useEvent((value) => {
    updateLocalPrefs(draft => {
      draft[`${effective_view}_folders`] = value;
    });
  });

  const handleNavigate = (parent_id) => {
    setFolderId(parent_id);
    const existingIndex = history.findLastIndex(h => h.parent === parent_id);
    if (existingIndex > -1) {
      updateHistory(draft => {
        return draft.slice(0, existingIndex + 1);
      });
    } else {
      updateHistory(draft => {
        return [...draft, { id: Date.now(), parent: parent_id }];
      });
    }
  }
  
  const { uid } = useUser();
  const { root, path } = getRootAndPath({ folder, uid });

  const handleBack = () => {
    if (history.length > 0) {
      updateHistory(draft => {
        return draft.slice(0, -1);
      });
      setFolderId(history[history.length - 2]?.parent || null);
    } else if (path.length > 0) {
      setFolderId(path[path.length - 1]._id);
    } else {
      setFolderId(null);
    }
  }

  const handleChooseLocation = () => {
    callback({
      folder: folder || null,
      setLoading,
      closeModal,
    });
  }

  const [openNewFolderModal] = useNewFolderModal();

  const handleNewFolder = () => {
    openNewFolderModal({ folder_id: folder?._id || null, view: effective_view, callback: ({ id }) => {
      // refetch();
      handleNavigate(id);
    }});
  }

  const { handleOnClick: handleOnClickExpandHistory } = useMenu({
    menuItems: path.map((f) => {
      return {
        label: f.title,
        icon: f._is_shared ? "folder-account" : "folder",
        onClick: () => {
          handleNavigate(f._id)
        }
      }
    }),
    rightHanded: false
  });

  const folder_title = folder && <>
    <HeaderPillButton text={folder.title} disabled={true}/>
    {folder._is_shared && <RoundIconButton icon="account-multiple-outline" className="small small_icon header_title_share_icon" disabled={true}/>}
  </>

  const breadcrumbs = folder && <>
    <HeaderPillButton text={root === "shared" ? "Shared with me" : (root === "trash" ? "Trash" : `My ${root}`)} onClick={() => {setFolderId(null); if (root === "shared") {setView("shared")}; updateHistory([]);}}/>
    <Icon name="chevron-right" className="header_title_chevron"/>
    {path.length === 1 && <HeaderPillButton text={path[0].title} onClick={() => {handleNavigate(path[0]._id)}}/>}
    {path.length > 1 && <RoundIconButton icon="dots-horizontal" className="small header_title_more_icon" onClick={handleOnClickExpandHistory} onContextMenu={handleOnClickExpandHistory}/>}
    {path.length > 0 && <Icon name="chevron-right" className="header_title_chevron"/>}
    {folder_title}
  </>;

  const show_breadcrumbs = useBreakpoint((width) => width > 700);

  const tabs = [
    ...(type === "scenario" ? [{name: "My scenarios", id: "scenarios", icon: "forum-outline"}] : []),
    ...(type === "rubric" ? [{name: "My rubrics", id: "rubrics", icon: "bullseye-arrow"}] : []),
    {name: "Shared with me", id: "shared", icon: "account-multiple-outline"}
  ];

  const headerComponent = !folder ? <Tabs tabs={tabs} activeTab={view} setActiveTab={(id) => {setView(id); updateHistory([]);}}/> : (
    <div className="file_picker_breadcrumbs">
      <RoundIconButton icon="arrow-left" className="small small_icon" onClick={handleBack}/>
      {show_breadcrumbs ? breadcrumbs : folder_title}
    </div>
  );

  const is_valid_and_has_permission = (!folder && ["scenarios", "rubrics"].includes(effective_view)) || (folder && ["edit", "owner"].includes(folder._level));
  const moving_within_itself = folder && moving_id && (folder.ancestors.includes(moving_id) || moving_id == folder._id);

  const is_valid_choose_location = is_valid_and_has_permission && !moving_within_itself && !folder?.trashed;

  const banner = (!is_valid_choose_location && (mode === "move" || mode === "save") && folder) && (
    <div className="callout_hint_box">
      {moving_within_itself ? (
        <div>
          <div><Icon name="cancel"/></div>
          <div>{`You can't move a folder inside itself.`}</div>
        </div>
      ) : (
        folder?.trashed ? (
          <div>
            <div><Icon name="trash-can-outline"/></div>
            <div>{`This folder is in the trash${["move", "save"].includes(mode) ? `, so you can't ${mode} files here.` : `.`}`}</div>
          </div>
        ) : (
          <div>
            <div><Icon name="lock-outline"/></div>
            <div>{`You don't have permission to ${mode === "move" ? "move" : "save"} files here. You can only view.`}</div>
          </div>
        )
      )}
    </div>
  )

  return (
    <Modal
      title={title || (mode === "move" ? `Move "${truncateString(file_title, 100)}"` : (mode === "save" ? `Choose where to save` : `Choose a ${type}`))}
      file_picker={true}
      headerComponent={headerComponent}
      is_loading={isFetching}
      scroll_location_key={`${instance_id}_${history_id}`}
    >
      <ModalContent>
        {isError ? 
          <GenericEmptyListPlaceholder error={true} handleClick={() => { setFolderId(null); setView(type+"s"); updateHistory([]); refetch(); }} /> : 
          (data && 
            <FileBrowserList filePicker={{ handleClick, type, disable_files: (mode === "move" || mode === "save"), moving_id }} files={files} view={effective_view} sort={sort} setSort={setSort} folders={folders} setFolders={setFolders} folder={folder} banner={banner}/>
          )
        }
      </ModalContent>
      <ModalButtons space_between={true}>
        {(!(effective_view === "shared" && !folder) && ["move", "save"].includes(mode)) ? <Button text="New folder" icon="folder-plus-outline" onClick={handleNewFolder} disabled={!is_valid_choose_location}/> : <div></div>}
        <div className="buttons">
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          {mode === "move" && <Button text="Move here" className="primary" onClick={handleChooseLocation} disabled={!is_valid_choose_location}/>}
          {mode === "save" && <Button text="Save here" className="primary" onClick={handleChooseLocation} disabled={!is_valid_choose_location}/>}
        </div>
      </ModalButtons>
    </Modal>
  )
}